import { fetchDetailPermissionsQueryOptions } from '@/api/permissionsQueryOptions';
import { fetchRegistrationTransferExcelFile, fetchRegistrationTransferFile, RegistrationTransfer, RegistrationTransferType } from '@/api/registration';
import { registrationTransfersQueryOptions, useCreateGrinsExportMutation } from '@/api/registrationQueryOptions';
import { ApiErrorComponent } from '@/components/ApiErrorComponent';
import useGridPageSize from '@/components/hooks/useGridPageSize';
import { usePersistedVisibilityModel } from '@/components/hooks/usePersistedVisibilityModel';
import useResponsive from '@/components/hooks/useResponsive';
import LoadProgress from '@/components/LoadProgress';
import GridToolbarPaginationButton from '@/components/mui-x/toolbar/GridToolbarPaginationButton';
import { exportBlob } from '@/lib/exportUtils';
import { getOrganization } from '@/store/auth/authSlice';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Box, Button, Tooltip } from '@mui/material';
import { GridActionsCellItem, GridColDef, GridColumnVisibilityModel, GridLogicOperator, GridPagination, GridRowId, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton } from '@mui/x-data-grid';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { deDE } from '@mui/x-data-grid-premium/locales';
import { Add } from '@nine-thirty-five/material-symbols-react/sharp';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Buffer } from 'buffer';
import { t } from 'i18next';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { useSelector } from 'react-redux';

export const Route = createFileRoute('/_auth/grinsExports')({
    component: GrinsExportsComponent,
    errorComponent: ({ error }) => {
        if (error) {
            return <ApiErrorComponent error={error as Error} />
        } else {
            return <ApiErrorComponent />
        }
    },
})

function GrinsExportsComponent() {

    const sessionOrganization = useSelector(getOrganization);

    const registrationTransferQuery = useSuspenseQuery(registrationTransfersQueryOptions(RegistrationTransferType.GRINS, 'OUT'))
    const registrationTransfers = registrationTransferQuery.data;

    const permissionsQuery = useSuspenseQuery(fetchDetailPermissionsQueryOptions('organization', sessionOrganization.uuid, ['RegistrationTransfer', 'RegistrationSnapshot']))
    const permissions = permissionsQuery.data

    const createSnapshot = permissions && permissions.filter(p => p.detail === 'RegistrationSnapshot' && p.permissions.some(a => a === 'create')).length > 0

    const columns: GridColDef[] = [];

    columns.push(
        {
            field: 'fileName',
            headerName: 'Datei',
            minWidth: 300
        },
        {
            field: 'fileSize',
            headerName: 'Größe',
            type: 'number',
        },
        {
            field: 'createdBy',
            headerName: 'Erstellt von',
            minWidth: 250
        },
        {
            field: 'createDate',
            headerName: 'Datum',
            valueGetter: (value) => value && new Date(value),
            type: 'dateTime',
            minWidth: 150
        },
    )

    const downloadFile = (row: RegistrationTransfer) => () => {
        fetchRegistrationTransferFile(row.uuid || '').then((file) => {
            const base64 = file.base64 || ''
            // console.log(base64)
            const raw = Buffer.from(base64, 'base64')
            // console.log(raw)
            const blob = new Blob([raw]);
            exportBlob(blob, file.filename || 'grins_export.csv');
        })
    }

    const downloadExcelFile = (row: RegistrationTransfer) => () => {
        fetchRegistrationTransferExcelFile(row.uuid || '').then((file) => {
            const base64 = file.base64 || ''
            // console.log(base64)
            const raw = Buffer.from(base64, 'base64')
            // console.log(raw)
            const blob = new Blob([raw]);
            exportBlob(blob, file.filename || 'grins_export.xls');
        })
    }

    columns.push(
        {
            field: 'actions',
            type: 'actions',
            headerName: t('column.download'),
            width: 100,
            cellClassName: 'actions',

            getActions: ({ id, row }: { id: GridRowId, row: RegistrationTransfer }) => {
                return [
                    <GridActionsCellItem
                        icon={<Tooltip title={t('action.downloadCSV')}><FileDownloadIcon /></Tooltip>}
                        label={t('action.edit')}
                        key={`download-${id}`}
                        className="textPrimary"
                        onClick={downloadFile(row)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={<Tooltip title={t('action.downloadExcel')}><DownloadForOfflineIcon /></Tooltip>}
                        label={t('action.edit')}
                        key={`excelDownload-${id}`}
                        className="textPrimary"
                        onClick={downloadExcelFile(row)}
                        color="inherit"
                    />
                ]
            }
        }
    )


    const initialVisibilityModel = {
        'fileName': true,
        'fileSize': true,
    } as GridColumnVisibilityModel

    const [visibilityModel, setVisibilityModel] = usePersistedVisibilityModel('registration', initialVisibilityModel)
    const handleVisibilityModelChange = React.useCallback((model: GridColumnVisibilityModel) => {
        setVisibilityModel(model)
    }, [setVisibilityModel]);

    const defaultLocaleText = deDE.components.MuiDataGrid.defaultProps.localeText
    const localeText = { ...defaultLocaleText, ...{ toolbarExportLabel: 'Export' } }

    const gridPageSize = useGridPageSize()
    const pageSizeOptions = gridPageSize.getPageSizeOptions('grinsExport', 35)
    const [paginationModel, setPaginationModel] = React.useState(gridPageSize.getPaginationModel('grinsExport', 35));

    const responsive = useResponsive();

    const createGrinsExportMutation = useCreateGrinsExportMutation(() => {
        enqueueSnackbar(t('action.grinsExportCreationSuccess'), { variant: 'success' });
    },
        (error) => {
            enqueueSnackbar(t('action.grinsExportCreationFailed') + error.message, { variant: 'error' })
        });

    function CreateGrinsExportButton() {
        return (
            <Tooltip title={t('button.grinsExport.create')}>
                <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                        createGrinsExportMutation.mutate()
                        enqueueSnackbar(t('action.grinsExportCreationTriggered'));
                    }}
                    startIcon={<Add width={20} />}>
                    {t('button.grinsExport.create')}
                </Button>
            </Tooltip>
        )
    }

    const CustomToolbar = () => {
        return (
            <GridToolbarContainer>
                <GridToolbarColumnsButton />
                {responsive.minMedium &&
                    <>
                        <GridToolbarFilterButton />
                        {createSnapshot &&
                            <CreateGrinsExportButton />
                        }

                    </>
                }
                {responsive.minMedium &&
                    <GridPagination />
                }
                {(responsive.xsmall || responsive.small) &&
                    <GridToolbarPaginationButton />
                }

            </GridToolbarContainer>
        )
    }

    if (registrationTransferQuery.isLoading || registrationTransferQuery.isFetching || !registrationTransfers) {
        return <LoadProgress />
    }

    return (
        <Box sx={{
            height: { xs: `calc(100% - 50px)` }
        }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <DataGridPremium
                    columns={columns}
                    rows={registrationTransfers}
                    getRowId={(row) => { return row.uuid || '' }}
                    localeText={localeText}
                    pagination
                    pageSizeOptions={pageSizeOptions}
                    paginationModel={paginationModel}
                    onPaginationModelChange={(model) => {
                        setPaginationModel(model)
                        gridPageSize.handlePaginationModelChange('awards', model)
                    }}
                    slots={{ toolbar: CustomToolbar }}
                    slotProps={{
                        filterPanel: {
                            logicOperators: [GridLogicOperator.And],
                            sx: { maxWidth: '100vw' }
                        },
                    }}
                    density='compact'
                    columnVisibilityModel={visibilityModel}
                    onColumnVisibilityModelChange={handleVisibilityModelChange}
                    hideFooter={true}
                    disableDensitySelector
                    disableAggregation
                    disableColumnPinning
                    disableColumnReorder
                    disableRowGrouping
                    disablePivoting
                    showToolbar />
            </div>
        </Box>
    );
}
